import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Candidate } from "@/types/candidate";
import { SkillBadge } from "@/components/SkillBadge";
import { ViewMode } from "./types";
import { User, Circle, Briefcase } from "lucide-react";
import { 
  Pagination, 
  PaginationContent, 
  PaginationItem, 
  PaginationLink, 
  PaginationNext, 
  PaginationPrevious 
} from "@/components/ui/pagination";
import { normalizeSkill } from "@/utils/skillNormalization";
import { matchesCandidateSearch } from "@/utils/searchUtils";

interface CandidateSearchResultsProps {
  searchQuery: string;
  onResultClick: (id: string) => void;
  viewMode: ViewMode;
}

export function CandidateSearchResults({ searchQuery, onResultClick, viewMode }: CandidateSearchResultsProps) {
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCandidates, setTotalCandidates] = useState(0);
  const resultsPerPage = 5;

  useEffect(() => {
    // Reset to first page on new search
    setCurrentPage(1);
  }, [searchQuery]);

  useEffect(() => {
    const searchCandidates = async () => {
      if (!searchQuery.trim()) return;

      setIsLoading(true);
      
      try {
        // Get all candidates and filter client-side for universal search
        const { data: allCandidates, error } = await supabase
          .from("candidates")
          .select("id, name, email, function, skills, status")
          .order("name", { ascending: true });
          
        if (error) throw error;
        
        // Filter using universal search
        const filteredCandidates = (allCandidates || []).filter(candidate => 
          matchesCandidateSearch(candidate, searchQuery)
        );
        
        setTotalCandidates(filteredCandidates.length);
        
        // Apply pagination to the filtered results
        const startIndex = (currentPage - 1) * resultsPerPage;
        const paginatedResults = filteredCandidates.slice(startIndex, startIndex + resultsPerPage);
        
        setCandidates(paginatedResults);
      } catch (error) {
        console.error("Error searching candidates:", error);
      } finally {
        setIsLoading(false);
      }
    };

    searchCandidates();
  }, [searchQuery, currentPage]);

  // Get status color based on candidate status
  const getStatusColor = (status?: string) => {
    switch (status?.toLowerCase()) {
      case "looking":
        return "text-green-500";
      case "open":
        return "text-orange-400";
      case "unavailable":
        return "text-red-500";
      default:
        return "text-gray-400";
    }
  };

  const totalPages = Math.ceil(totalCandidates / resultsPerPage);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    // Direct scroll without any transition
    const scrollableParent = document.querySelector('.overflow-y-auto') as HTMLElement;
    if (scrollableParent) {
      // Force immediate scroll without animation
      scrollableParent.style.scrollBehavior = 'auto';
      scrollableParent.scrollTop = 0;
      scrollableParent.style.scrollBehavior = '';
    }
  };

  if (isLoading) {
    return <div className="flex items-center justify-center h-40 text-gray-500">
      <div className="animate-spin mr-2 h-5 w-5 border-2 border-digger-blue border-t-transparent rounded-full"></div>
      Searching candidates...
    </div>;
  }

  if (candidates.length === 0) {
    return <div className="flex flex-col items-center justify-center h-40 text-gray-500">
      <User className="h-12 w-12 mb-2 text-gray-400" />
      <p>No candidates found matching "<span className="font-medium">{searchQuery}</span>"</p>
    </div>;
  }

  const renderPagination = () => {
    if (totalPages <= 1) return null;
    
    return (
      <div className="sticky bottom-0 z-10 bg-white dark:bg-gray-800">
        <Pagination>
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious 
                onClick={() => handlePageChange(Math.max(currentPage - 1, 1))}
                className={currentPage === 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}
              />
            </PaginationItem>
            
            {[...Array(Math.min(3, totalPages))].map((_, i) => {
              const pageNumber = i + 1;
              return (
                <PaginationItem key={pageNumber}>
                  <PaginationLink
                    onClick={() => handlePageChange(pageNumber)}
                    isActive={currentPage === pageNumber}
                    className="cursor-pointer"
                  >
                    {pageNumber}
                  </PaginationLink>
                </PaginationItem>
              );
            })}
            
            {totalPages > 3 && (
              <PaginationItem>
                <PaginationLink
                  onClick={() => handlePageChange(totalPages)}
                  isActive={currentPage === totalPages}
                  className="cursor-pointer"
                >
                  {totalPages}
                </PaginationLink>
              </PaginationItem>
            )}
            
            <PaginationItem>
              <PaginationNext 
                onClick={() => handlePageChange(Math.min(currentPage + 1, totalPages))}
                className={currentPage === totalPages ? "pointer-events-none opacity-50" : "cursor-pointer"}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full">
      {viewMode === "card" ? (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4">
            {candidates.map((candidate) => (
              <div
                key={candidate.id}
                className="p-4 border rounded-sm hover:bg-gray-50 cursor-pointer transition-colors shadow-sm hover:shadow"
                onClick={() => onResultClick(candidate.id)}
              >
                <div className="flex items-center mb-2">
                  <div className="h-10 w-10 bg-gray-100 rounded-sm flex items-center justify-center mr-3 text-digger-blue">
                    <User className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="font-medium text-digger-blue">{candidate.name}</h3>
                    {candidate.function && (
                      <p className="text-gray-600 text-sm flex items-center">
                        <Briefcase className="h-3.5 w-3.5 mr-1 inline-block" />
                        {candidate.function}
                      </p>
                    )}
                  </div>
                  {candidate.status && (
                    <div className={`ml-auto flex items-center gap-1 ${getStatusColor(candidate.status)}`}>
                      <Circle className="h-3 w-3 fill-current" />
                      <span className="text-xs font-medium">{candidate.status}</span>
                    </div>
                  )}
                </div>
                
                {candidate.skills && candidate.skills.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1">
                    {candidate.skills.slice(0, 5).map((skill) => (
                      <SkillBadge key={skill} label={skill} />
                    ))}
                    {candidate.skills.length > 5 && (
                      <span className="text-xs text-gray-500 ml-1 self-center">
                        +{candidate.skills.length - 5} more
                      </span>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
          {renderPagination()}
        </>
      ) : (
        <>
          <div className="overflow-hidden">
            <div className="grid grid-cols-12 bg-muted px-4 py-2.5 text-sm font-medium sticky top-0">
              <div className="col-span-4">Name</div>
              <div className="col-span-3">Function</div>
              <div className="col-span-3">Skills</div>
              <div className="col-span-2">Status</div>
            </div>
            
            <div>
              {candidates.map((candidate) => {
                const statusColor = getStatusColor(candidate.status);
                
                return (
                  <div 
                    key={candidate.id} 
                    className="grid grid-cols-12 px-4 py-3 border-b hover:bg-muted/50 cursor-pointer transition-colors"
                    onClick={() => onResultClick(candidate.id)}
                  >
                    <div className="col-span-4 flex items-center gap-2">
                      <div className="h-8 w-8 bg-gray-100 rounded-sm flex items-center justify-center text-digger-blue">
                        <User className="h-4 w-4" />
                      </div>
                      <span className="font-medium">{candidate.name}</span>
                    </div>
                    <div className="col-span-3 flex items-center">
                      {candidate.function ? (
                        <div className="flex items-center">
                          <Briefcase className="h-4 w-4 text-gray-400 mr-2" />
                          {candidate.function}
                        </div>
                      ) : (
                        "-"
                      )}
                    </div>
                    <div className="col-span-3 flex flex-wrap gap-1 items-center">
                      {candidate.skills && candidate.skills.slice(0, 2).map((skill) => (
                        <SkillBadge key={skill} label={skill} />
                      ))}
                      {candidate.skills && candidate.skills.length > 2 && (
                        <span className="text-xs text-gray-500">
                          +{candidate.skills.length - 2} more
                        </span>
                      )}
                    </div>
                    <div className="col-span-2 flex items-center gap-1.5">
                      <Circle className={`h-3 w-3 fill-current ${statusColor}`} />
                      <span>{candidate.status || "No status"}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          {renderPagination()}
        </>
      )}
    </div>
  );
}
